import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js'
import {
  PaymentIntent,
  PaymentRequestPaymentMethodEvent,
  SetupIntent,
  StripeError,
  PaymentRequest,
  PaymentRequestOptions,
  PaymentRequestUpdateOptions,
  StripePaymentRequestButtonElementOptions,
} from '@stripe/stripe-js'

import { CustomDOMEvents } from '@/enums/customDOMEvents'
import { PageName } from '@/enums/pageName'
import {
  CheckoutFlowVersion,
  PaymentMethod as PaymentMethodEnum,
  PaymentPlatform,
} from '@/enums/payments'
import { canMakePayment } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { ApiService } from '@/api'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { useSession } from '@/core/session/useSession'
import { trackPaymentInfoAdded, trackPurchaseStarted } from '@/events/events'

import { TWalletPaymentButtonProps } from './types'

const getResultUrl = (
  paymentResultUrl: string,
  paymentMethod: PaymentMethodEnum,
  successPage: PageName
): URL => {
  const fail = PageName.paymentFailed
  const url = new URL(paymentResultUrl)
  url.searchParams.append('success_page', successPage)
  url.searchParams.append('fail_page', fail)
  url.searchParams.append('payment_method', paymentMethod)

  return url
}

const eventOnStripeWalletButtonClick = new Event(CustomDOMEvents.ON_STRIPE_WALLET_BUTTON_CLICK)

export const WalletPaymentButton = ({
  planData,
  getItem,
  setItem,
  showDelimiter,
  buttonsDelimiterText,
  pageName,
  config,
  stripeButtonTheme,
  getStripeWalletButtonStyles,
  onReady,
  successRedirectTo,
}: TWalletPaymentButtonProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null | undefined>()
  const [prOptionsSnapshot, setPROptionsSnapshot] = useState<string>('')

  const stripeButtonStyles = useMemo<StripePaymentRequestButtonElementOptions['style']>(
    () => getStripeWalletButtonStyles(stripeButtonTheme, CheckoutFlowVersion.V1),
    [getStripeWalletButtonStyles, stripeButtonTheme]
  )

  useEffect(() => {
    if (!stripe || !elements || !planData || !user) {
      return
    }

    if (paymentRequest === null) {
      return
    }

    // send trial price if exists else subscription price
    const amount = Number.isFinite(planData.trialPrice) ? planData.trialPrice : planData.price

    const prOptions: PaymentRequestOptions = {
      country: 'US',
      currency: planData.currency.toLowerCase(),
      total: {
        label: planData.orderPlanName,
        amount: parseFloat((amount * 100).toFixed(2)),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: ['link', 'browserCard'],
    }

    const prOptionsStr = JSON.stringify(prOptions)
    if (prOptionsStr === prOptionsSnapshot) {
      return
    }

    let pr: PaymentRequest
    if (paymentRequest) {
      pr = paymentRequest
      const updateOptions: PaymentRequestUpdateOptions = {
        currency: prOptions.currency,
        total: prOptions.total,
      }
      try {
        pr.update(updateOptions)
      } catch (error) {
        console.error(error)
        setPaymentRequest(null)
      }
    } else {
      pr = stripe.paymentRequest(prOptions)
    }
    setPROptionsSnapshot(prOptionsStr)

    canMakePayment(stripe, pr, !prOptionsSnapshot).then(
      (result) => {
        if (result && Object.keys(result).length) {
          setPaymentRequest(pr)
        } else {
          setPaymentRequest(null)
        }
      },
      () => {
        setPaymentRequest(null)
      }
    )
  }, [stripe, elements, planData, user, paymentRequest, prOptionsSnapshot])

  useEffect(() => {
    if (!paymentRequest || !stripe) {
      return
    }

    paymentRequest.on(
      'paymentmethod',
      async (paymentRequestEvent: PaymentRequestPaymentMethodEvent) => {
        resetContactStateCheckTimeout(setItem)

        let paymentMethod: PaymentMethodEnum
        switch (paymentRequestEvent.walletName) {
          case 'applePay':
            paymentMethod = PaymentMethodEnum.APPLE_PAY
            break
          case 'googlePay':
            paymentMethod = PaymentMethodEnum.GOOGLE_PAY
            break
          default:
            throw Error('Unsupported payment method')
        }
        setItem('paymentMethod', paymentMethod)
        setItem('paymentPlatform', PaymentPlatform.STRIPE)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const promisesList: Promise<any>[] = [
          trackPurchaseStarted(
            pageName,
            config.PARTNER_ID,
            config.APP_ID,
            config.PAGE_SET_ID,
            planData.internalPlanId,
            paymentMethod,
            PaymentPlatform.STRIPE,
            sessionContext,
            user
          ),
        ]

        promisesList.push(
          trackPaymentInfoAdded(
            pageName,
            config.PARTNER_ID,
            config.APP_ID,
            config.PAGE_SET_ID,
            planData.internalPlanId,
            paymentMethod,
            PaymentPlatform.STRIPE,
            sessionContext,
            config.PIXEL_ID,
            config.TWITTER_CONF?.pixel_id,
            config.TIKTOK_PIXEL_ID,
            user,
            [],
            [paymentRequestEvent.walletName]
          )
        )

        const redirectToResultPage = async (
          paymentResultUrl: string,
          type: string,
          clientSecret: string
        ) => {
          const redirectTo = getResultUrl(paymentResultUrl, paymentMethod, successRedirectTo)

          if (type === 'setup') {
            const intent = await stripe.retrieveSetupIntent(clientSecret)
            if (intent.error) {
              navigate(getPath(PageName.paymentFailed))
              return
            }
            redirectTo.searchParams.append('setup_intent', intent.setupIntent.id)
          } else {
            const intent = await stripe.retrievePaymentIntent(clientSecret)
            if (intent.error) {
              navigate(getPath(PageName.paymentFailed))
              return
            }
            redirectTo.searchParams.append('payment_intent', intent.paymentIntent.id)
          }
          Promise.all(promisesList).finally(() => {
            window.location.href = redirectTo.href
          })
        }

        console.log(paymentRequestEvent)
        const payload = {
          page_set_id: config.PAGE_SET_ID,
          plan_id: planData.internalPlanId,
          customer_name: paymentRequestEvent.payerName,
          wallet_name: paymentRequestEvent.walletName,
        }

        let response
        try {
          response = await ApiService.subscribePlan(payload)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (error.response?.status === 401) {
            navigate(getPath(PageName.registration))
            return
          }

          if (error.response?.status === 409) {
            navigate(getPath(successRedirectTo))
            return
          }
          console.log('error1', error)
          navigate(getPath(PageName.paymentFailed))
          return
        }

        const {
          type,
          client_secret: clientSecret,
          payment_result_url: paymentResultUrl,
        } = response.data
        let stripeError: StripeError | undefined, intent: SetupIntent | PaymentIntent | undefined
        const confirmSubscription =
          type === 'setup' ? stripe.confirmCardSetup : stripe.confirmCardPayment

        try {
          const result = await confirmSubscription(
            clientSecret,
            {
              payment_method: paymentRequestEvent.paymentMethod.id,
              return_url: getResultUrl(paymentResultUrl, paymentMethod, successRedirectTo).href,
            },
            { handleActions: false }
          )

          if (result.error !== undefined) {
            stripeError = result.error
          } else if ('paymentIntent' in result) {
            intent = result.paymentIntent
          } else {
            intent = result.setupIntent
          }

          if (stripeError) {
            paymentRequestEvent.complete('fail')
            redirectToResultPage(paymentResultUrl, type, clientSecret)
            // Show error to your customer (e.g., insufficient funds)
            return
          }
          if (!intent) {
            paymentRequestEvent.complete('fail')
            redirectToResultPage(paymentResultUrl, type, clientSecret)
            return
          }

          if (intent.status === 'requires_action') {
            const { error } = await confirmSubscription(clientSecret)
            if (error) {
              paymentRequestEvent.complete('fail')
              redirectToResultPage(paymentResultUrl, type, clientSecret)
            } else {
              // The payment has succeeded.
              redirectToResultPage(paymentResultUrl, type, clientSecret)
              paymentRequestEvent.complete('success')
            }
          } else {
            redirectToResultPage(paymentResultUrl, type, clientSecret)
            paymentRequestEvent.complete('success')
          }
        } catch (error) {
          console.error('error2', error)
          paymentRequestEvent.complete('fail')
          redirectToResultPage(paymentResultUrl, type, clientSecret)
        }
      }
    )
    return () => {
      if (paymentRequest) {
        paymentRequest.off('paymentmethod')
      }
    }
  }, [
    paymentRequest,
    stripe,
    planData,
    user,
    getItem,
    setItem,
    navigate,
    pageName,
    config,
    sessionContext,
    successRedirectTo,
  ])
  //missed dependencies
  //'currentPaymentMethod',

  const onClick = () => {
    document.dispatchEvent(eventOnStripeWalletButtonClick)
  }

  if (!paymentRequest) {
    return null
  }

  return (
    <>
      {showDelimiter && (
        <div className="wallet-payment-button-delimiter">{buttonsDelimiterText}</div>
      )}
      <div className="stripe-wallet-container">
        <PaymentRequestButtonElement
          options={{ paymentRequest, style: stripeButtonStyles }}
          onClick={onClick}
          onReady={onReady}
        />
      </div>
    </>
  )
}
